import React, { useState, useEffect } from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Spinner } from 'reactstrap';
import { useSelector, useDispatch } from "react-redux";
import { getActivityUsers } from "../../slices/thunks";
import moment from "moment";
import 'moment/locale/mn';
import ReactApexChart from "react-apexcharts";
import dummyImg from "../../assets/images/users/user-dummy-img.jpg";

moment.locale('mn');

const SparklineChart = React.memo(({ data, isOnline }) => {
    const counts = Array.isArray(data) ? data.map(d => d.count) : [];
    const dates = Array.isArray(data) ? data.map(d => d.date) : [];
    const color = isOnline ? '#00e1ff' : '#6c757d';
    const options = {
        chart: { type: 'area', sparkline: { enabled: true }, toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 800 } },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.03, stops: [0, 100] } },
        colors: [color],
        xaxis: { categories: dates },
        tooltip: {
            theme: false,
            custom: ({ series, seriesIndex, dataPointIndex }) => {
                const d = dates[dataPointIndex];
                const dateLabel = d ? moment(d).format('MM/DD (ddd)') : '';
                const count = series[seriesIndex][dataPointIndex];
                return `<div style="background:#0a0e17;border:1px solid rgba(0,225,255,0.4);border-radius:4px;padding:6px 10px;font-family:Courier New,monospace;">
                    <div style="color:rgba(0,225,255,0.7);font-size:10px;letter-spacing:1px;">${dateLabel}</div>
                    <div style="color:#00ff88;font-size:12px;font-weight:bold;">${count} үйлдэл</div>
                </div>`;
            }
        },
    };
    return (
        <ReactApexChart
            type="area"
            series={[{ name: 'Үйлдэл', data: counts }]}
            options={options}
            height={60}
        />
    );
});

const UserActivityOffcanvas = ({ isOpen, toggle }) => {
    const dispatch = useDispatch();
    const [ipInfos, setIpInfos] = useState({});

    const { activityData, loading } = useSelector((state) => ({
        activityData: state.Users.activityData,
        loading: state.Users.loading
    }));

    useEffect(() => {
        if (isOpen) {
            dispatch(getActivityUsers());
        }
    }, [dispatch, isOpen]);

    const getCleanIp = (rawIp) => {
        if (!rawIp) return null;
        // Split by comma (proxies) AND colon (port) to get only the IP
        return rawIp.split(',')[0].split(':')[0].trim();
    };

    const isInternalIP = (ip) => {
        if (!ip) return false;
        return ip.startsWith('192.168.') || ip.startsWith('10.') ||
            (ip.startsWith('172.') && parseInt(ip.split('.')[1]) >= 16 && parseInt(ip.split('.')[1]) <= 31) ||
            ip === '::1' || ip === '127.0.0.1' || ip.includes('localhost') || ip.includes('::ffff:127.0.0.1');
    };

    useEffect(() => {
        const fetchIpDetails = async () => {
            if (Array.isArray(activityData) && activityData.length > 0) {
                const ipsToFetch = [...new Set(activityData.map(u => getCleanIp(u.lastIp)).filter(ip => {
                    if (!ip) return false;
                    return !isInternalIP(ip);
                }))];

                const newIps = ipsToFetch.filter(ip => !ipInfos[ip]);
                if (newIps.length > 0) {
                    const newInfoMap = {};
                    await Promise.allSettled(newIps.map(async (ip) => {
                        try {
                            // Try ip-api.com first
                            const res = await fetch(`http://ip-api.com/json/${ip}`);
                            if (res.ok) {
                                const json = await res.json();
                                if (json.status === "success") {
                                    newInfoMap[ip] = {
                                        country: json.country || "MONGOLIA",
                                        city: json.city || "ULAANBAATAR",
                                        isp: json.isp || "UNITEL"
                                    };
                                    return;
                                }
                            }

                            // Fallback 1: ipapi.co
                            const res2 = await fetch(`https://ipapi.co/${ip}/json/`);
                            if (res2.ok) {
                                const json2 = await res2.json();
                                if (!json2.error) {
                                    newInfoMap[ip] = {
                                        country: json2.country_name || "MONGOLIA",
                                        city: json2.city || "ULAANBAATAR",
                                        isp: json2.org || "UNITEL"
                                    };
                                    return;
                                }
                            }

                            // Fallback 2: freeipapi.com
                            const res3 = await fetch(`https://freeipapi.com/api/json/${ip}`);
                            if (res3.ok) {
                                const json3 = await res3.json();
                                newInfoMap[ip] = {
                                    country: json3.countryName || "MONGOLIA",
                                    city: json3.cityName || "ULAANBAATAR",
                                    isp: json3.asName || "UNITEL"
                                };
                            }
                        } catch (err) {
                            console.error(`IP Lookup fail for ${ip}:`, err);
                        }
                    }));
                    if (Object.keys(newInfoMap).length > 0) {
                        setIpInfos((prev) => ({ ...prev, ...newInfoMap }));
                    }
                }
            }
        };
        fetchIpDetails();
    }, [activityData, ipInfos]);

    const formatActionTime = (user) => {
        const target = user.lastSeen || user.lastLogin || user.lastAction;
        if (!target) return "Мэдээлэл байхгүй";
        return moment(target).format("YYYY-MM-DD HH:mm");
    };

    const formatTimeAgo = (user) => {
        const target = user.lastSeen || user.lastLogin || user.lastAction;
        if (!target) return "НЭВТРЭЭГҮЙ";
        return moment(target).fromNow();
    };

    return (
        <Offcanvas isOpen={isOpen} direction="end" toggle={toggle} className="border-0 shadow-lg" style={{
            width: '420px',
            backgroundColor: '#0a0e17',
            boxShadow: '-10px 0 30px rgba(0, 225, 255, 0.1)',
            borderLeft: '1px solid rgba(0, 225, 255, 0.2)'
        }}>
            <OffcanvasHeader toggle={toggle} className="pb-3 border-bottom" style={{
                borderBottomColor: 'rgba(0, 225, 255, 0.3) !important',
                background: 'linear-gradient(90deg, #0a0e17 0%, rgba(0, 150, 255, 0.1) 100%)'
            }}>
                <span className="fw-bold fs-16 d-flex align-items-center" style={{ color: '#00e1ff', letterSpacing: '2px', textTransform: 'uppercase' }}>
                    <i className="ri-radar-line me-2 fs-22 flash-animation"></i> USER MONITORING
                </span>
            </OffcanvasHeader>
            <OffcanvasBody className="p-0 custom-scrollbar" style={{ overflowY: 'auto', backgroundColor: '#0a0e17' }}>
                <style>
                    {`
                    .flash-animation { animation: flash 2s infinite alternate; }
                    @keyframes flash { 0% { opacity: 0.5; text-shadow: 0 0 5px #00e1ff; } 100% { opacity: 1; text-shadow: 0 0 15px #00e1ff; } }
                    .hud-card { transform-style: preserve-3d; transition: all 0.3s ease; }
                    .hud-card:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 5px 20px rgba(0, 225, 255, 0.15); border-color: rgba(0, 225, 255, 0.5) !important; }
                    .hud-text { font-family: 'Courier New', monospace; letter-spacing: 0.5px; }
                    .data-bar { position: relative; height: 2px; width: 100%; background: rgba(0, 225, 255, 0.1); margin-top: 5px; overflow: hidden; }
                    .data-bar::after { content: ''; position: absolute; top: 0; left: 0; height: 100%; width: 30%; background: #00e1ff; animation: scan 3s linear infinite; }
                    @keyframes scan { 0% { left: -30%; } 100% { left: 100%; } }
                `}
                </style>
                {(loading && (!activityData || activityData.length === 0)) ? (
                    <div className="d-flex justify-content-center align-items-center h-100 flex-column">
                        <Spinner color="info" />
                        <span className="text-info mt-3 hud-text fs-12">СИСТЕМ РҮҮ НЭВТРЭХ ГЭЖ БАЙНА...</span>
                    </div>
                ) : (
                    <div className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom border-secondary border-opacity-25">
                            <h6 className="hud-text text-white-50 fs-11 fw-bold mb-0">ТӨЛӨВ: <span className="text-info">ИДЭВХТЭЙ</span></h6>
                            <h6 className="hud-text text-white-50 fs-11 fw-bold mb-0">ХЭРЭГЛЭГЧИД: <span className="text-white">{Array.isArray(activityData) ? activityData.length : 0}</span></h6>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            {Array.isArray(activityData) && activityData.length > 0 ? (
                                activityData.map((user, index) => {
                                    const cleanIp = getCleanIp(user.lastIp);
                                    const internalIp = isInternalIP(cleanIp);

                                    return (
                                        <div key={index} className="card hud-card mb-0 position-relative" style={{
                                            backgroundColor: 'rgba(10, 14, 23, 0.8)',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            borderRadius: '8px',
                                            overflow: 'hidden'
                                        }}>
                                            {/* HUD Corner Decor */}
                                            <div style={{ position: 'absolute', top: 0, left: 0, width: '15px', height: '15px', borderLeft: '2px solid #00e1ff', borderTop: '2px solid #00e1ff' }}></div>
                                            <div style={{ position: 'absolute', bottom: 0, right: 0, width: '15px', height: '15px', borderRight: '2px solid #00e1ff', borderBottom: '2px solid #00e1ff' }}></div>

                                            <div className="card-body p-3">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="position-relative me-3">
                                                        <div style={{ padding: '2px', borderRadius: '50%', border: `1px solid ${user.isOnline ? '#00ff88' : '#495057'}` }}>
                                                            <img src={user.image || dummyImg} alt={user.name} className="rounded-circle avatar-sm" style={{ objectFit: 'cover' }} onError={(e) => { e.target.onerror = null; e.target.src = dummyImg }} />
                                                        </div>
                                                        <span className={`position-absolute bottom-0 end-0 p-1 bg-${user.isOnline ? 'success' : 'secondary'} border border-dark rounded-circle`} style={{ width: '12px', height: '12px', boxShadow: user.isOnline ? '0 0 8px #00ff88' : 'none' }}></span>
                                                    </div>
                                                    <div className="flex-grow-1 overflow-hidden">
                                                        <h5 className="fs-13 mb-0 text-truncate fw-bold text-white" style={{ letterSpacing: '0.5px' }}>{user.name.toUpperCase()}</h5>
                                                        <p className="mb-0 fs-10 text-truncate" style={{ color: '#00e1ff' }}>{user.company || user.email}</p>
                                                    </div>
                                                    <div className="flex-shrink-0 text-end">
                                                        <span className="hud-text px-2 py-1 fs-10" style={{ backgroundColor: user.isOnline ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: user.isOnline ? '#00ff88' : '#adb5bd', border: `1px solid ${user.isOnline ? '#00ff88' : '#adb5bd'}`, borderRadius: '4px' }}>
                                                            {user.isOnline ? 'СҮЛЖЭЭНД' : 'САЛСАН'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="p-2 mb-2 rounded" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.03)' }}>
                                                    <div className="row g-0 align-items-center">
                                                        <div className="col-5">
                                                            <p className="hud-text text-white-50 mb-1 fs-10">ХОЛБОГДСОН</p>
                                                            <h6 className="hud-text text-white mb-0 fs-11" title={formatActionTime(user)}>{formatTimeAgo(user).toUpperCase()}</h6>
                                                        </div>
                                                        <div className="col-2 text-center text-secondary">
                                                            <i className="ri-arrow-right-s-line"></i>
                                                        </div>
                                                        <div className="col-5 text-end">
                                                            <p className="hud-text text-white-50 mb-1 fs-10">НИЙТ ХАНДАЛТ</p>
                                                            <h6 className="hud-text mb-0 fs-11" style={{ color: '#00e1ff' }}>{user.activityCount || 0} OP</h6>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 30-day sparkline */}
                                                <div className="mt-2 mb-2" style={{ borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.04)', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                                    <p className="hud-text mb-0 px-2 pt-2 fs-9 text-uppercase" style={{ color: 'rgba(0, 225, 255, 0.5)' }}>
                                                        <i className="ri-bar-chart-2-line me-1"></i>СҮҮЛИЙН 30 ХОНОГИЙН ИДЭВХИ
                                                    </p>
                                                    <SparklineChart data={user.dailyActivity} isOnline={user.isOnline} />
                                                </div>

                                                {/* Connection Metadata Section */}
                                                <div className="p-2 mb-2 rounded" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(0, 225, 255, 0.1)' }}>
                                                    <div className="row g-0 align-items-center text-center">
                                                        <div className="col-4 border-end border-secondary border-opacity-25">
                                                            <p className="hud-text text-white-50 mb-0 fs-8 text-uppercase" style={{ fontSize: '8px' }}>ТӨХӨӨРӨМЖ</p>
                                                            <h6 className="hud-text text-info mb-0 fs-10 fw-bold" style={{ fontSize: '10px' }}>
                                                                <i className={`ri-${user.device?.type === 'desktop' ? 'computer' : 'smartphone'}-line me-1`}></i>
                                                                {user.device?.type?.toUpperCase() || "N/A"}
                                                            </h6>
                                                        </div>
                                                        <div className="col-4 border-end border-secondary border-opacity-25">
                                                            <p className="hud-text text-white-50 mb-0 fs-8 text-uppercase" style={{ fontSize: '8px' }}>СИСТЕМ</p>
                                                            <h6 className="hud-text text-white mb-0 fs-10 fw-bold" style={{ fontSize: '10px' }}>
                                                                <i className="ri-window-line me-1"></i>
                                                                {user.os?.short_name || user.os?.name || "N/A"}
                                                            </h6>
                                                        </div>
                                                        <div className="col-4">
                                                            <p className="hud-text text-white-50 mb-0 fs-8 text-uppercase" style={{ fontSize: '8px' }}>ХӨТӨЧ</p>
                                                            <h6 className="hud-text text-warning mb-0 fs-10 fw-bold" style={{ fontSize: '10px' }}>
                                                                <i className="ri-chrome-line me-1"></i>
                                                                {user.client?.name || "N/A"}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* IP and ISP info display - HUD Style */}
                                                <div className="p-2 rounded position-relative overflow-hidden" style={{ backgroundColor: 'rgba(0, 225, 255, 0.05)', border: '1px solid rgba(0, 225, 255, 0.2)' }}>
                                                    {user.isOnline && <div className="data-bar" style={{ position: 'absolute', bottom: 0, left: 0 }}></div>}
                                                    <div className="row g-0 align-items-center">
                                                        <div className="col-5">
                                                            <p className="hud-text mb-1 fs-9 text-uppercase" style={{ color: 'rgba(0, 225, 255, 0.6)', letterSpacing: '1px' }}>
                                                                <i className="ri-map-pin-line me-1"></i>БАЙРШИЛ
                                                            </p>
                                                            <h6 className="hud-text mb-0 fs-11 text-white text-truncate pe-2 fw-bold">
                                                                {!cleanIp ? "ТОДОРХОЙГҮЙ" : internalIp ? "ULAANBAATAR (LOCAL)" : (
                                                                    <span>
                                                                        {ipInfos[cleanIp]?.city && `${ipInfos[cleanIp]?.city.toUpperCase()}, `}
                                                                        {ipInfos[cleanIp]?.country?.toUpperCase() || "ШИНЖИЛЖ БАЙНА..."}
                                                                    </span>
                                                                )}
                                                            </h6>
                                                        </div>
                                                        <div className="col-7 text-end border-start border-secondary border-opacity-25 ps-2">
                                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                                <p className="hud-text mb-0 fs-9 text-uppercase" style={{ color: 'rgba(0, 225, 255, 0.6)' }}><i className="ri-router-line me-1"></i>ISP</p>
                                                                <span className="hud-text fs-10 fw-bold" style={{ color: '#00ff88', textShadow: '0 0 5px rgba(0, 255, 136, 0.3)' }}>
                                                                    {internalIp ? "UNITEL (LOCAL)" : ipInfos[cleanIp]?.isp?.toUpperCase() || "МЭДЭЭЛЭЛГҮЙ"}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex justify-content-between align-items-center bg-dark bg-opacity-50 p-1 rounded-1" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                <span className="hud-text text-white-50 fs-7">IP_ADDR:</span>
                                                                <span className="hud-text text-info fs-9 fw-bold font-monospace">{cleanIp && cleanIp !== '0.0.0.0' ? cleanIp : "NOT_SYNCED"}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="text-center py-5">
                                    <div className="avatar-lg mx-auto mb-3">
                                        <div className="avatar-title bg-dark text-info rounded-circle fs-24 border border-info border-opacity-25">
                                            <i className="ri-radar-line flash-animation"></i>
                                        </div>
                                    </div>
                                    <h5 className="hud-text text-white-50 fs-13">ХАЙЛТЫН ҮР ДҮН ОЛДСОНГҮЙ</h5>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default UserActivityOffcanvas;
